import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import Icon from '../common/Icon';
import ConnectionStatus from '../common/ConnectionStatus';
import './Header.css';

const Header = ({ onOpenCommandPalette }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { currentTenant, tenants, selectTenant, exitImpersonation, isImpersonating } = useTenant();
  const { user, logout, isAuthenticated } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  };

  return (
    <header className="header">
      <div className="header-left">
        <div className="logo">Auth1</div>
        <div className="context-indicator">
          <span className="viewing-as">Viewing:</span>
          <select
            className="tenant-dropdown"
            id="tenant-selector"
            value={currentTenant}
            onChange={(e) => selectTenant(e.target.value)}
            aria-label="Select tenant"
          >
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
          {isImpersonating && (
            <button className="exit-btn" onClick={exitImpersonation}>
              Exit View
            </button>
          )}
        </div>
      </div>

      <div className="header-right">
        {/* Real-time Connection Status */}
        <ConnectionStatus size="sm" />

        {/* Search Button (Opens Command Palette) */}
        <button
          className="header-icon-btn"
          aria-label="Search (Ctrl+K)"
          onClick={onOpenCommandPalette}
          title="Search (Ctrl+K)"
        >
          <Icon name="search" />
        </button>

        {/* Notifications */}
        <button className="header-icon-btn" aria-label="Notifications">
          <Icon name="bell" />
          <span className="notification-badge">3</span>
        </button>

        {/* Theme Toggle */}
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label="Toggle dark mode"
        >
          {theme === 'light' ? (
            <Icon name="sun" className="sun-icon" />
          ) : (
            <Icon name="moon" className="moon-icon" />
          )}
        </button>

        {/* User Menu */}
        <div className="user-menu-container" ref={userMenuRef}>
          <button
            className="user-avatar-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-label="User menu"
            aria-expanded={showUserMenu}
          >
            <div className="user-avatar">
              {getUserInitials()}
              <div className="status-dot"></div>
            </div>
          </button>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">{getUserInitials()}</div>
                <div className="user-dropdown-info">
                  <div className="user-dropdown-name">{user?.name || 'User'}</div>
                  <div className="user-dropdown-email">{user?.email || ''}</div>
                </div>
              </div>
              <div className="user-dropdown-divider"></div>
              <button className="user-dropdown-item" onClick={() => { setShowUserMenu(false); navigate('/settings'); }}>
                <Icon name="settings" />
                <span>Settings</span>
              </button>
              <button className="user-dropdown-item" onClick={() => { setShowUserMenu(false); navigate('/activity'); }}>
                <Icon name="activity" />
                <span>Activity Log</span>
              </button>
              <div className="user-dropdown-divider"></div>
              <button className="user-dropdown-item danger" onClick={handleLogout}>
                <Icon name="log-out" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
